import ClassNames from "Helpers/Common";
import { StatusBadge, StatCard, SectionTable } from "../components";
import { formatUptime } from "../format";

const ProbeCard = ({ probe }) => (
  <div className="rounded-lg bg-white flex flex-col px-6 py-4 gap-2 shadow-lg">
    <div className="flex items-center justify-between">
      <span className="text-base font-medium sapphire">{probe.name}</span>
      <StatusBadge status={probe.status} />
    </div>
    <div className="text-sm text-gray-500">
      {probe.latencyMs != null ? `${probe.latencyMs} ms` : "not checked"}
      {probe.detail ? ` · ${probe.detail}` : ""}
    </div>
  </div>
);

/* System — infrastructure + third-party dependency health. Real-time, no range.
   Every card explains how the check works; version + error-rate up top. */
export const SystemTab = ({ data }) => {
  const proc = data.infrastructure?.process;
  const v = data.version || {};
  const err = data.errorRate || {};

  return (
    <>
      <div className="flex items-center gap-3">
        <StatusBadge status={data.overall} />
        <span className="text-sm text-gray-400 capitalize">{data.environment}</span>
      </div>

      {/* error-rate + process summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label="Errors (last hour)"
          value={err.lastHour ?? "—"}
          sub="5xx responses"
          alert={(err.lastHour || 0) > 0}
        />
        <StatCard
          label="API Uptime"
          value={formatUptime(proc?.uptimeSec)}
          sub={v.startedAt ? `since ${new Date(v.startedAt).toLocaleString()}` : ""}
        />
        <StatCard
          label="Memory"
          value={`${proc?.memoryRssMb} MB`}
          sub={`heap ${proc?.heapUsedMb} MB · ${proc?.nodeVersion}`}
        />
      </div>

      <div className="flex flex-col gap-4">
        <span className="text-lg font-medium sapphire">Infrastructure</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.infrastructure.probes.map((p) => (
            <ProbeCard key={p.name} probe={p} />
          ))}
        </div>
        {data.infrastructure.queues?.length > 0 && (
          <SectionTable headers={["Queue", "Waiting", "Active", "Delayed", "Failed"]}>
            {data.infrastructure.queues.map((q) => (
              <tr key={q.name}>
                <td className="px-6 py-3 text-sm font-medium sapphire">{q.name}</td>
                <td className="px-6 py-3 text-sm text-gray-500">{q.waiting}</td>
                <td className="px-6 py-3 text-sm text-gray-500">{q.active}</td>
                <td className="px-6 py-3 text-sm text-gray-500">{q.delayed}</td>
                <td
                  className={ClassNames(
                    "px-6 py-3 text-sm",
                    q.failed > 0 ? "text-red-600 font-medium" : "text-gray-500"
                  )}
                >
                  {q.failed}
                </td>
              </tr>
            ))}
          </SectionTable>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <span className="text-lg font-medium sapphire">Third-party Services</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.services.map((p) => (
            <ProbeCard key={p.name} probe={p} />
          ))}
        </div>
      </div>
    </>
  );
};
